<template>
    <div id="app" class="app">
        <router-view class="app-content"/>
        <div class="loading-box" v-show="$store.getters.loading">
            <LoadingIcon class="loading-icon" />
        </div>
        <transition-group
            name="notification"
            tag="div"
            class="notification-stack"
            :class="{ 'map-context': isMapNotificationContext }"
        >
            <div
                v-for="notification in notifications"
                :key="notification.id"
                class="notification-item"
                :class="{
                    persistent: notification.persistent,
                    'instant-dismiss': instantClosingIds[notification.id],
                    'has-action': !!notification.actionText,
                    'has-close': notification.dismissible,
                }"
                @click="handleNotificationClick(notification)"
            >
                <span class="notification-message">{{ notification.message }}</span>
                <button
                    v-if="notification.actionText"
                    v-active-touch
                    class="notification-action interactive-text"
                    type="button"
                    @click.stop="handleNotificationAction(notification)"
                >
                    {{ notification.actionText }}
                </button>
                <button
                    v-if="notification.dismissible"
                    v-active-touch
                    class="notification-close interactive-icon"
                    type="button"
                    aria-label="Close notification"
                    @click.stop="dismissNotification(notification.id, true)"
                ></button>
            </div>
        </transition-group>
    </div>
</template>·

<script>
import { mapGetters } from 'vuex';
import LoadingIcon from "@/components/icons/LoadingIcon.vue";

export default {
    components: {
        LoadingIcon
    },
    data() {
        return {
            instantClosingIds: {},
        };
    },
    computed: {
        ...mapGetters('appConfig', ['brand_name', 'brand_favicon']),
        notifications() {
            return this.$store.getters.notifications;
        },
        isMapNotificationContext() {
            return this.$route && this.$route.path === '/cabinet_map';
        },
    },
    mounted() {
        this.hideLoading();
        document.title = this.brand_name;
        const favicon = document.querySelector("link[rel~='icon']");
        if (favicon) {
            favicon.href = this.brand_favicon;
        }
    },
    methods: {
        dismissNotification(id, instant = false) {
            if (instant) {
                this.$set(this.instantClosingIds, id, true);
                setTimeout(() => {
                    this.$delete(this.instantClosingIds, id);
                }, 0);
            }
            this.$store.commit('REMOVE_NOTIFICATION', id);
        },
        handleNotificationAction(notification) {
            if (notification.dismissible) {
                this.dismissNotification(notification.id);
            }
            if (typeof notification.action === 'function') {
                notification.action();
            }
        },
        handleNotificationClick(notification) {
            if (notification.dismissible && !notification.persistent) {
                this.dismissNotification(notification.id);
            }
        },
    },
};
</script>

<style lang="scss">
.app {
    min-height: 100%;
    overflow: hidden;
    position: relative;
    background-color: white;
}

.app-content {
    height: 100%;
}

.loading-box {
    position: fixed;
    background: rgba(255, 255, 255, 0.42);
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
}

.loading-icon {
    width: 36px;
    height: 36px;
    animation: loading 0.9s linear 0s infinite;
    -webkit-animation: loading 0.9s linear 0s infinite;
    -o-animation: loading 0.9s linear 0s infinite;
    -moz-animation: loading 0.9s linear 0s infinite;
}

.notification-stack {
    position: fixed;
    left: 0;
    right: 0;
    z-index: 1002;
    top: calc(52px + env(safe-area-inset-top));
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    pointer-events: none;
}

.notification-stack.map-context {
    top: calc(60px + env(safe-area-inset-top));
}

.notification-item {
    width: min(100%, 480px);
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(17, 20, 27, 0.78);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #fff;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    column-gap: 14px;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 500;
    line-height: 19px;
    pointer-events: auto;
    user-select: none;
    -webkit-user-select: none;
}

.notification-item.has-action,
.notification-item.has-close {
    grid-template-columns: minmax(0, 1fr) auto;
}

.notification-item.has-action.has-close {
    grid-template-columns: minmax(0, 1fr) auto auto;
}

.notification-item * {
    user-select: none;
    -webkit-user-select: none;
}

.notification-item.persistent {
    background: rgba(17, 20, 27, 0.78);
}

.notification-message {
    min-width: 0;
    overflow-wrap: anywhere;
}

.notification-action {
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.92);
    padding: 0;
    font-size: 13px;
    font-weight: 700;
    line-height: 19px;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-color: rgba(255, 255, 255, 0.6);
    white-space: nowrap;
}

.notification-close {
    position: relative;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    padding: 0;
    justify-self: end;
}

.notification-close::before,
.notification-close::after {
    content: "";
    position: absolute;
    left: 6px;
    top: 10px;
    width: 10px;
    border-top: 1.4px solid rgba(255, 255, 255, 0.88);
}

.notification-close::before {
    transform: rotate(45deg);
}

.notification-close::after {
    transform: rotate(-45deg);
}

.notification-enter-active,
.notification-leave-active {
    transition: transform 220ms ease, opacity 180ms ease;
}

.notification-enter {
    opacity: 0;
    transform: translateY(-12px);
}

.notification-leave-to {
    opacity: 0;
    transform: translateX(28px);
}

.notification-item.instant-dismiss.notification-leave-active {
    transition: none;
}

.notification-move {
    transition: transform 180ms ease;
}
</style>
